import { useEffect, useMemo, useRef } from 'react';
import { AllMapLayers, MapLayer } from 'features/mapping/mapping.constants';
import { useExternalFeatureLayers } from 'features/mapping/hooks/useExternalFeatureLayers';
import { EXTERNAL_FEATURE_MAP_LAYER } from 'features/wildfires/wildfire.constants';
import { LayerConfig } from 'features/wildfires/wildfire.types';
import { useMapLayersState } from 'state/useMapLayersState';
import { getData, LOCAL_STORAGE_KEY } from 'state/localStorage';
import { useHistory, useLocation } from 'react-router-dom';
import { defer } from 'es-toolkit/compat';

type LocationState = {
  skipSetLayersConfig?: boolean;
};

const isExternalLayer = (layer: MapLayer): boolean =>
  layer === EXTERNAL_FEATURE_MAP_LAYER;

const getInitialActiveLayersMap = (
  skipSetLayersConfig: boolean | undefined,
  mapLayers: MapLayer[],
  mainMapActiveLayers: Record<MapLayer, boolean> | undefined,
): Map<string, boolean> => {
  if (skipSetLayersConfig) {
    // skipSetLayersConfig was set to true either by a navigation action
    // or during initial mount, so it's safe to use mapLayers directly
    // to build the initial active layers map
    return new Map(mapLayers.map((layer) => [layer, true]));
  }

  if (mainMapActiveLayers) {
    return new Map(
      Object.entries(mainMapActiveLayers)
        .filter(([, enabled]) => enabled)
        .map(([layer, enabled]) => [layer, enabled]),
    );
  }

  return new Map();
};

const getLayersToToggle = (input: {
  layers: MapLayer[];
  externalFeatureLayerIds: string[];
  initialActiveLayers: Map<string, boolean>;
  action: 'off' | 'on';
}): string[] => {
  const { layers, externalFeatureLayerIds, initialActiveLayers, action } =
    input;

  return layers.flatMap((layer) => {
    if (isExternalLayer(layer)) {
      return externalFeatureLayerIds.filter((layerId) =>
        action === 'on'
          ? !initialActiveLayers.has(layerId)
          : initialActiveLayers.has(layerId),
      );
    }

    const hasLayer = initialActiveLayers.has(layer);
    if (action === 'on') {
      return hasLayer ? [] : [layer];
    }
    return hasLayer ? [layer] : [];
  });
};

export const useProgressionModelLayersConfig = (config: LayerConfig): void => {
  const { disable, enable, turnOff, turnOn } = config;
  const { mapLayers, setMapLayerVisibility, setDisabledLayers, setMapLayers } =
    useMapLayersState();
  const { externalFeatureLayers, isLoading: isLoadingExternalFeatureLayers } =
    useExternalFeatureLayers();
  const { state: locationState = {} } = useLocation<LocationState>();
  const history = useHistory();

  const initializationState = useRef({
    hasDisabled: false,
    hasTurnedOff: false,
    hasTurnedOn: false,
  });

  const { skipSetLayersConfig } = locationState;

  useEffect(() => {
    if (skipSetLayersConfig) {
      return () => {};
    }

    const timer = defer(() => {
      // Defer to ensure:
      // 1. Initial synchronous setup of layers config completes first.
      // 2. Then we update history state to mark `skipSetLayersConfig: true`.
      // This guarantees the layers config is initialized only once when:
      //    - navigating to a progression model page,
      //    - reloading the page, or
      //    - opening another model within the model page.
      history.replace({
        ...history.location,
        state: {
          // Preserve existing location state (e.g., `skipMapCentering`)
          // to avoid overwriting settings that control behavior such as
          // map centering when directly linking models.
          ...(history.location.state ?? {}),
          skipSetLayersConfig: true,
        },
      });
    });

    return () => {
      clearTimeout(timer);
    };
  }, [history, skipSetLayersConfig]);

  const mainMapActiveLayers = useRef(
    getData<Record<MapLayer, boolean>>(LOCAL_STORAGE_KEY.MAP_LAYERS),
  );

  const initialActiveLayersMap = useRef(
    getInitialActiveLayersMap(
      skipSetLayersConfig,
      mapLayers,
      mainMapActiveLayers.current,
    ),
  );

  const externalFeatureLayerIds = useMemo(
    () => externalFeatureLayers.map((layer) => layer.mapLayerId),
    [externalFeatureLayers],
  );

  const layersToTurnOff = useMemo(
    () =>
      getLayersToToggle({
        layers: turnOff,
        externalFeatureLayerIds,
        initialActiveLayers: initialActiveLayersMap.current,
        action: 'off',
      }),
    [turnOff, externalFeatureLayerIds],
  );

  const layersToTurnOn = useMemo(
    () =>
      getLayersToToggle({
        layers: turnOn,
        externalFeatureLayerIds,
        initialActiveLayers: initialActiveLayersMap.current,
        action: 'on',
      }),
    [turnOn, externalFeatureLayerIds],
  );

  const layersToDisable = useMemo(() => {
    const baseLayers = AllMapLayers.filter(
      (layer) => disable.includes(layer) && !enable.includes(layer),
    );

    const shouldDisableExternal =
      disable.includes(EXTERNAL_FEATURE_MAP_LAYER) &&
      !enable.includes(EXTERNAL_FEATURE_MAP_LAYER);

    return [
      ...baseLayers,
      ...(shouldDisableExternal ? externalFeatureLayerIds : []),
    ];
  }, [disable, enable, externalFeatureLayerIds]);

  // If not skipping layer config setup (e.g., navigated from the wildfire page),
  // initialize with main map layers as the default state
  useEffect(() => {
    if (
      !mainMapActiveLayers.current ||
      typeof skipSetLayersConfig === 'undefined' ||
      skipSetLayersConfig
    ) {
      return;
    }

    setMapLayers((currentLayers) => ({
      ...currentLayers,
      ...mainMapActiveLayers.current,
    }));
  }, [setMapLayers, skipSetLayersConfig]);

  // Handle layer config update in sequence
  useEffect(() => {
    if (isLoadingExternalFeatureLayers) return;

    const { hasDisabled, hasTurnedOff, hasTurnedOn } =
      initializationState.current;

    // Step 1: Disable layers
    if (!hasDisabled) {
      initializationState.current.hasDisabled = true;
      setDisabledLayers(layersToDisable);
    }

    // Skip further steps if skipSetLayersConfig is either undefined or true
    if (typeof skipSetLayersConfig === 'undefined' || skipSetLayersConfig) {
      return;
    }

    // Step 2: Turn off layers
    if (!hasTurnedOff) {
      initializationState.current.hasTurnedOff = true;
      layersToTurnOff.forEach((layer) => setMapLayerVisibility(layer, false));
    }

    // Step 3: Turn on layers
    if (!hasTurnedOn) {
      initializationState.current.hasTurnedOn = true;
      layersToTurnOn.forEach((layer) => setMapLayerVisibility(layer, true));
    }
  }, [
    isLoadingExternalFeatureLayers,
    skipSetLayersConfig,
    layersToDisable,
    layersToTurnOff,
    layersToTurnOn,
    setDisabledLayers,
    setMapLayerVisibility,
  ]);
};
